import { useState } from "react";
import type { CaseView } from "../api";
import { api } from "../api";
import { SectionLabel } from "./chrome";

/* ═══════════════════════════════════════════════════════════════════════
   Something the customer has to READ.

   Every other surface in this app takes something from the person. This
   one gives them something and records that they saw it — a consent to
   treat, a costs disclosure, a credit guide, a duty of disclosure. In
   several regulated fields nobody may lawfully act until that has
   happened.

   The module behind it has existed and worked for a long time. Nothing
   rendered it, so it returned `open: true` into a void: a business could
   configure a consent form, crAIg would put it "on screen", and the
   customer would see nothing while the case blocked forever on a gap
   nobody could clear. This is that missing screen.

   ── It shows the stored text, never the template ───────────────────────
   The words come from what the module SAVED when it was shown, not from
   the business's current config. A practice that edits its consent form
   tomorrow does not retroactively change what this person read, and the
   reviewer's copy is the same string. That property is the entire reason
   this is worth doing properly rather than rendering a config field.

   ── Why the button is not the only way to agree ────────────────────────
   Agreeing is a claim someone may have to stand behind, so it is a
   deliberate act on the actual words: shown in full, scrollable, never
   summarised, never behind a link, and never pre-agreed. The same
   reasoning as the declaration next to it.
   ═══════════════════════════════════════════════════════════════════════ */

export function Presenter({
  caseId,
  presenter,
  onAgreed,
}: {
  caseId: string;
  presenter: NonNullable<CaseView["presenter"]>;
  onAgreed(updated: CaseView): void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const agreed = Boolean(presenter.agreedAt);

  const agree = async () => {
    setBusy(true);
    setError(null);
    try {
      const { case: updated } = await api.agreePresenter(caseId);
      onAgreed(updated);
    } catch (e) {
      setError((e as Error).message || "That didn't go through. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="panel px-4 py-4"
      style={agreed ? undefined : { borderColor: "var(--accent)" }}
    >
      <SectionLabel>{presenter.title}</SectionLabel>

      {presenter.why && (
        <p style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 6, lineHeight: 1.5 }}>
          {presenter.why}
        </p>
      )}

      {/*
        In full, and scrollable rather than truncated. A document folded
        behind "read more" is a document nobody read, and the whole value
        of this surface is being able to say they saw it.
      */}
      <div
        className="mt-3"
        style={{
          maxHeight: 320,
          overflowY: "auto",
          background: "var(--surface-2)",
          border: "1px solid var(--line)",
          borderRadius: "var(--radius)",
          padding: "0.85rem 1rem",
          fontSize: 13.5,
          lineHeight: 1.6,
          // The business wrote this as prose with its own line breaks.
          whiteSpace: "pre-wrap",
        }}
      >
        {presenter.text}
      </div>

      {error && (
        <p role="alert" style={{ fontSize: 13, color: "var(--danger)", marginTop: 8 }}>
          {error}
        </p>
      )}

      {agreed ? (
        <p style={{ fontSize: 13, color: "var(--ok)", marginTop: 10 }}>
          ✓ Agreed{presenter.agreedAt ? ` · ${new Date(presenter.agreedAt).toLocaleString()}` : ""}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2.5" style={{ marginTop: 10 }}>
          <button type="button" className="btn btn-primary btn-term" disabled={busy} onClick={agree}>
            {busy ? "…" : "I've read this and I agree"}
          </button>
          {/* Said plainly rather than implied. Somebody who does not want
              to agree should know what happens, not guess. */}
          <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
            {presenter.mustAgree
              ? "Nothing can go ahead until you do — ask crAIg if anything isn't clear."
              : "You can carry on without agreeing if you'd rather."}
          </span>
        </div>
      )}
    </section>
  );
}
